/**
 * Admin V3 — Drivers module — Search/filter/export bar
 *
 * Layout (left → right):
 *   [Search input] [Status filter dropdown] [Result counter] [Exporter CSV button]
 *
 * Search: parent owns the debounced state; this component only renders the
 * input and emits onChange. Backend has NO search parameter for /admin/drivers,
 * so the filtering is applied client-side over the loaded page (transparent
 * to the operator via the placeholder copy "Rechercher sur la page (...)").
 *
 * Status filter: 5 options (all + 4 statuses from driverStatusMeta).
 * Result counter: visible only when filter is active.
 * CSV export: inline axios.get with responseType: 'blob'; uses useToast on error.
 *
 * All class strings mirror BookingsListSearchBar (Phase 2.2 precedent) to
 * preserve operator UX consistency across V3 modules.
 */

import React, { useState, useCallback } from 'react';
import axios from 'axios';
import { Search, X, Download, Loader2 } from 'lucide-react';
import { useAuth } from '../../../context/AuthContext';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../../components/ui/select';
import { driverStatusMeta } from '../../shared/visualTokens';
import { useToast } from '../../shared/hooks/useToast';
import { t } from '../../i18n';

const API_URL = import.meta.env.VITE_BACKEND_URL + '/api';

const STATUS_FILTER_VALUES = ['all', 'available', 'busy', 'offline', 'suspended'];

const DriversListSearchBar = ({
  search,
  onSearchChange,
  statusFilter,
  onStatusFilterChange,
  resultsCount,
  isFilterActive,
  loading,
  locale = 'fr',
}) => {
  const { getAuthHeaders } = useAuth();
  const { showError } = useToast();
  const [exporting, setExporting] = useState(false);

  const handleExportCsv = useCallback(async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const r = await axios.get(`${API_URL}/admin/drivers/export/csv`, {
        headers: getAuthHeaders(),
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'drivers.csv';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      showError(t('drivers.toast.csv.error', locale));
    } finally {
      setExporting(false);
    }
  }, [exporting, getAuthHeaders, showError, locale]);

  const resultsCountText = t('drivers.results.count', locale).replace('{n}', String(resultsCount));

  return (
    <div className="flex flex-wrap items-center gap-3">
      {/* Search (client-side, page-scoped) */}
      <div className="relative flex-1 max-w-md min-w-[240px]">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
        <input
          type="text"
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder={t('drivers.search.placeholder', locale)}
          data-testid="adminv3-drivers-search"
          className="w-full rounded-xl border border-slate-200 bg-slate-50 py-2.5 pl-10 pr-9 text-sm text-slate-900 outline-none focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100"
        />
        {search && (
          <button
            type="button"
            onClick={() => onSearchChange('')}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
            data-testid="adminv3-drivers-search-clear"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Status filter (server-side) */}
      <Select value={statusFilter} onValueChange={onStatusFilterChange}>
        <SelectTrigger className="h-10 w-[200px] rounded-xl border-slate-200 bg-white text-sm text-slate-700" data-testid="adminv3-drivers-status-filter">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {STATUS_FILTER_VALUES.map((v) => (
            <SelectItem key={v} value={v}>
              {v === 'all' ? t('drivers.filter.all', locale) : (driverStatusMeta[v]?.label || v)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {/* Result counter (only when filter is active and not loading) */}
      {isFilterActive && !loading && (
        <span className="text-sm text-slate-400" data-testid="adminv3-drivers-results-count">
          {resultsCountText}
        </span>
      )}

      {/* Export CSV button (right-aligned) */}
      <button
        type="button"
        onClick={handleExportCsv}
        disabled={exporting}
        data-testid="adminv3-drivers-export-csv"
        className="ml-auto inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-50"
      >
        {exporting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
        {t('drivers.export.csvFull', locale)}
      </button>
    </div>
  );
};

export default DriversListSearchBar;
